"use client";

import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import "./CampaignDetailsModal.css";

const FOLLOWER_LABELS = {
  1000: "1,000+",
  5000: "5,000+",
  10000: "10,000+",
  25000: "25,000+",
  50000: "50,000+",
  100000: "100,000+",
  500000: "500,000+",
  1000000: "1,000,000+",
};

const CITY_LABELS = {
  damascus: "Damascus",
  aleppo: "Aleppo",
  homs: "Homs",
  hama: "Hama",
  latakia: "Latakia",
  tartus: "Tartus",
  deir_ez_zor: "Deir ez-Zor",
  raqqa: "Raqqa",
  idlib: "Idlib",
  daraa: "Daraa",
  as_suwayda: "As-Suwayda",
  quneitra: "Quneitra",
  al_hasakah: "Al-Hasakah",
  rif_dimashq: "Rif Dimashq",
};

function formatCity(value) {
  if (!value) return "—";
  const key = String(value).trim().toLowerCase();
  if (CITY_LABELS[key]) return CITY_LABELS[key];
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatFollowers(value) {
  if (value == null || value === "") return "—";
  const n = String(value).replace(/\D/g, "");
  if (n && FOLLOWER_LABELS[n]) return FOLLOWER_LABELS[n];
  return String(value);
}

function formatGender(g) {
  const map = { male: "Male", female: "Female", both: "Both" };
  return map[g] || g || "—";
}

function formatContent(req) {
  const map = {
    reel: "Reel",
    story: "Story",
    reel_story: "Reel & Story",
    ad: "Ad",
  };
  return map[req] || req || "—";
}

function formatOffer(key) {
  const map = { cash: "Cash", service: "Service", gift: "Gift" };
  return map[key] || key;
}

function formatObjective(role) {
  if (!role) return "";
  const r = String(role);
  if (r.startsWith("other:")) return `Other: ${r.slice(6)}`;
  const map = {
    sales: "Sales",
    increase_followers: "Increase followers",
    brand_awareness: "Brand awareness",
    other: "Other",
  };
  return map[r] || r.replace(/_/g, " ");
}

function normalizeRoles(roles) {
  if (!roles) return [];
  if (Array.isArray(roles)) return roles.filter(Boolean);
  if (typeof roles === "string") {
    try {
      const p = JSON.parse(roles);
      return Array.isArray(p) ? p : [roles];
    } catch {
      return roles.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
}

function normalizeOffers(offers) {
  if (!offers) return [];
  if (Array.isArray(offers)) return offers.filter(Boolean);
  if (typeof offers === "string") {
    try {
      const p = JSON.parse(offers);
      return Array.isArray(p) ? p : [offers];
    } catch {
      return offers.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
}

function mediaUrl(path) {
  if (!path) return null;
  if (String(path).startsWith("http")) return path;
  return `https://api.tafaseelapp.com/storage/${path}`;
}

/** Build a clickable URL from stored username or partial URL */
function socialHref(value, platform) {
  if (!value || !String(value).trim()) return null;
  const s = String(value).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const noProto = s.replace(/^\/+/, "");
  if (
    platform === "instagram" &&
    /instagram\.com/i.test(noProto)
  ) {
    return `https://${noProto.replace(/^https?:\/\//i, "")}`;
  }
  if (
    platform === "facebook" &&
    (/facebook\.com/i.test(noProto) || /^fb\.com/i.test(noProto))
  ) {
    return `https://${noProto.replace(/^https?:\/\//i, "")}`;
  }
  const user = s.replace(/^@/, "");
  if (platform === "instagram") return `https://www.instagram.com/${user}`;
  return `https://www.facebook.com/${user}`;
}

export default function CampaignDetailsModal({
  modal_center,
  setmodal_center,
  tog_center,
  campaign,
  onStatusUpdate,
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  const updateCampaignStatus = async (newStatus) => {
    if (!campaign || !campaign.id) return;

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/campaigns/${campaign.id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(`Campaign status updated to ${newStatus}`, {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: "colored",
        });

        if (onStatusUpdate) {
          onStatusUpdate(campaign.id, newStatus);
        }

        tog_center();
      } else {
        toast.error(
          response.data.message || "Failed to update campaign status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating campaign status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const approveCampaign = async () => {
    if (!campaign || !campaign.id) return;

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.post(
        `/admin/campaigns/${campaign.id}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Campaign approved successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        if (onStatusUpdate) {
          onStatusUpdate(campaign.id, "active");
        }

        tog_center();
      } else {
        toast.error(response.data.message || "Failed to approve campaign", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error approving campaign:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const rejectCampaign = async () => {
    if (!campaign || !campaign.id) return;

    if (
      !window.confirm(
        "Are you sure you want to reject this campaign? This action cannot be undone."
      )
    ) {
      return;
    }

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.post(
        `/admin/campaigns/${campaign.id}/reject`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Campaign rejected successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        if (onStatusUpdate) {
          onStatusUpdate(campaign.id, "cancelled");
        }

        tog_center();
      } else {
        toast.error(response.data.message || "Failed to reject campaign", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error rejecting campaign:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: "secondary", text: "Draft" },
      need_approve: { color: "warning", text: "Needs Approval" },
      active: { color: "success", text: "Active" },
      inactive: { color: "secondary", text: "Inactive" },
      suspended: { color: "warning", text: "Suspended" },
      paused: { color: "info", text: "Paused" },
      completed: { color: "primary", text: "Completed" },
      cancelled: { color: "danger", text: "Cancelled" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      text: "Unknown",
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatPrice = (price) => {
    const n = Number(price);
    const safe = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(safe);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!campaign) return null;

  const isNeedingApproval = campaign.status === "need_approve";
  const user = campaign.user ?? null;
  const employerOffers = normalizeOffers(campaign.employer_offer);
  const objectives = normalizeRoles(campaign.campaign_roles);
  const thumb = campaign.image_url ? mediaUrl(campaign.image_url) : null;

  return (
    <Modal
      isOpen={modal_center}
      toggle={tog_center}
      centered
      size="lg"
      className="admin-detail-modal campaign-details-modal"
    >
      <ModalHeader toggle={tog_center} className="border-0">
        <div className="cdm-header-inner d-flex align-items-center flex-wrap gap-2">
          <i className="ri-megaphone-line" aria-hidden />
          <span>Campaign Details</span>
          {isNeedingApproval && (
            <Badge color="warning" className="ms-0">
              <i className="ri-time-line me-1"></i>
              Needs Approval
            </Badge>
          )}
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="campaign-details">
          <div className="cdm-hero">
            {thumb && (
              <img
                src={thumb}
                alt={campaign.title || "Campaign"}
                className="cdm-hero-thumb"
              />
            )}
            <div className="flex-grow-1 min-w-0">
              <h5 className="cdm-hero-title">{campaign.title || "—"}</h5>
              <p className="cdm-hero-desc">
                {campaign.description || "No description provided."}
              </p>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {getStatusBadge(campaign.status)}
                {campaign.category && (
                  <Badge color="light" className="text-dark border">
                    {campaign.category.name}
                  </Badge>
                )}
              </div>
            </div>
            <div className="cdm-price-pill d-none d-md-flex">
              <strong>{formatPrice(campaign.price)}</strong>
              <small>Budget</small>
            </div>
          </div>

          <Row className="g-3">
            <Col lg={6}>
              <section className="cdm-section">
                <h6 className="cdm-section-head">
                  <i className="ri-crosshair-2-line"></i>
                  Targeting &amp; requirements
                </h6>
                <dl className="cdm-kv mb-0">
                  <dt>Target audience</dt>
                  <dd>{campaign.target_audience || "—"}</dd>
                  <dt>City</dt>
                  <dd>{formatCity(campaign.city)}</dd>
                  <dt>Gender</dt>
                  <dd>{formatGender(campaign.gender)}</dd>
                  <dt>Min. followers</dt>
                  <dd>{formatFollowers(campaign.number_of_followers)}</dd>
                  <dt>Content</dt>
                  <dd>{formatContent(campaign.content_requirements)}</dd>
                  <dt>Location</dt>
                  <dd>{campaign.location || "—"}</dd>
                </dl>
              </section>
            </Col>
            <Col lg={6}>
              <section className="cdm-section">
                <h6 className="cdm-section-head">
                  <i className="ri-gift-line"></i>
                  Offer &amp; objectives
                </h6>
                <dl className="cdm-kv mb-3">
                  <dt>Employer offer</dt>
                  <dd>
                    {employerOffers.length ? (
                      <div className="cdm-badges">
                        {employerOffers.map((o) => (
                          <Badge key={o} color="primary" pill className="fw-normal">
                            {formatOffer(o)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </dd>
                  <dt>Campaign objective</dt>
                  <dd>
                    {objectives.length ? (
                      <div className="cdm-badges">
                        {objectives.map((o, i) => (
                          <Badge key={`${o}-${i}`} color="info" pill className="fw-normal text-dark">
                            {formatObjective(o)}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </dd>
                  <dt>Offer details</dt>
                  <dd>{campaign.offer_details?.trim() || "—"}</dd>
                </dl>
              </section>
            </Col>
            <Col lg={6}>
              <section className="cdm-section">
                <h6 className="cdm-section-head">
                  <i className="ri-links-line"></i>
                  Social &amp; links
                </h6>
                <dl className="cdm-kv mb-0">
                  <dt>Facebook</dt>
                  <dd>
                    {campaign.facebook_account ? (
                      <a
                        href={socialHref(campaign.facebook_account, "facebook")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cdm-link"
                      >
                        {campaign.facebook_account}
                      </a>
                    ) : (
                      "—"
                    )}
                  </dd>
                  <dt>Instagram</dt>
                  <dd>
                    {campaign.instagram_account ? (
                      <a
                        href={socialHref(campaign.instagram_account, "instagram")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cdm-link"
                      >
                        {campaign.instagram_account}
                      </a>
                    ) : (
                      "—"
                    )}
                  </dd>
                </dl>
              </section>
            </Col>
            <Col lg={6}>
              <section className="cdm-section">
                <h6 className="cdm-section-head">
                  <i className="ri-calendar-schedule-line"></i>
                  Campaign meta
                </h6>
                <dl className="cdm-kv mb-0">
                  <dt>Campaign ID</dt>
                  <dd>#{campaign.id}</dd>
                  <dt>Campaign date</dt>
                  <dd>{formatDateTime(campaign.campaign_date)}</dd>
                  <dt>Views</dt>
                  <dd>{campaign.views ?? 0}</dd>
                  <dt>Created</dt>
                  <dd>{formatDateTime(campaign.created_at)}</dd>
                  <dt>Updated</dt>
                  <dd>{formatDateTime(campaign.updated_at)}</dd>
                </dl>
              </section>
            </Col>
          </Row>

          <section className="cdm-section">
            <h6 className="cdm-section-head">
              <i className="ri-building-4-line"></i>
              Business
            </h6>
            {user ? (
              <dl className="cdm-kv mb-0">
                <dt>Name</dt>
                <dd>{user.name ?? "—"}</dd>
                {user.company_name && (
                  <>
                    <dt>Company</dt>
                    <dd>{user.company_name}</dd>
                  </>
                )}
                <dt>Email</dt>
                <dd>{user.email ?? "—"}</dd>
                {user.phone && (
                  <>
                    <dt>Phone</dt>
                    <dd>{user.phone}</dd>
                  </>
                )}
              </dl>
            ) : (
              <p className="text-muted mb-0 small">No business user linked.</p>
            )}
          </section>

          {campaign.rules && (
            <section className="cdm-section">
              <h6 className="cdm-section-head">
                <i className="ri-file-list-3-line"></i>
                Campaign rules
              </h6>
              <div className="p-3 rounded bg-light border small mb-0">
                {campaign.rules}
              </div>
            </section>
          )}

          <section className="cdm-section">
            <h6 className="cdm-section-head">
              <i className="ri-bar-chart-2-line"></i>
              Applications summary
            </h6>
            <div className="cdm-stat-grid">
              <div className="cdm-stat">
                <h4>{campaign.applications_count ?? 0}</h4>
                <small>Total</small>
              </div>
              <div className="cdm-stat">
                <h4 className="text-warning">
                  {campaign.pending_applications ?? 0}
                </h4>
                <small>Pending</small>
              </div>
              <div className="cdm-stat">
                <h4 className="text-success">
                  {campaign.approved_applications ?? 0}
                </h4>
                <small>Approved</small>
              </div>
              <div className="cdm-stat">
                <h4 className="text-danger">
                  {campaign.rejected_applications ?? 0}
                </h4>
                <small>Rejected</small>
              </div>
            </div>
          </section>

          {campaign.applications && campaign.applications.length > 0 && (
            <section className="cdm-section">
              <h6 className="cdm-section-head">
                <i className="ri-team-line"></i>
                Recent applications
              </h6>
              <div>
                {campaign.applications.slice(0, 5).map((application) => {
                  const inf = application?.influencer;
                  return (
                    <div key={application.id} className="cdm-app-row">
                      <div>
                        <strong>{inf?.name ?? "—"}</strong>
                        <br />
                        <small className="text-muted">{inf?.email ?? ""}</small>
                        {application.message && (
                          <div>
                            <small className="text-muted">
                              &ldquo;{application.message}&rdquo;
                            </small>
                          </div>
                        )}
                      </div>
                      <div className="text-end">
                        <Badge
                          color={
                            application.status === "approved"
                              ? "success"
                              : application.status === "rejected"
                              ? "danger"
                              : "warning"
                          }
                        >
                          {application.status}
                        </Badge>
                        <br />
                        <small className="text-muted">
                          {formatDateTime(application.created_at)}
                        </small>
                      </div>
                    </div>
                  );
                })}
                {campaign.applications.length > 5 && (
                  <div className="text-center pt-2">
                    <small className="text-muted">
                      And {campaign.applications.length - 5} more…
                    </small>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </ModalBody>

      <ModalFooter className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-3">
        {isNeedingApproval ? (
          <div className="cdm-footer-actions flex-grow-1">
            <h6 className="mb-2 text-warning">
              <i className="ri-time-line me-2"></i>
              Approval required
            </h6>
            <div className="d-flex flex-wrap gap-2">
              <Button
                color="success"
                onClick={approveCampaign}
                disabled={isUpdating}
                className="rounded-pill px-3"
              >
                <i className="ri-check-line me-1"></i>
                {isUpdating ? "Approving…" : "Approve"}
              </Button>
              <Button
                color="danger"
                outline
                onClick={rejectCampaign}
                disabled={isUpdating}
                className="rounded-pill px-3"
              >
                <i className="ri-close-line me-1"></i>
                {isUpdating ? "Rejecting…" : "Reject"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="cdm-footer-actions flex-grow-1">
            <h6 className="mb-2">Update status</h6>
            <div className="d-flex flex-wrap gap-2">
              {campaign.status !== "active" && (
                <Button
                  color="success"
                  size="sm"
                  className="rounded-pill"
                  onClick={() => updateCampaignStatus("active")}
                  disabled={isUpdating}
                >
                  <i className="ri-play-line me-1"></i>
                  Active
                </Button>
              )}
              {campaign.status !== "suspended" && (
                <Button
                  color="warning"
                  size="sm"
                  className="rounded-pill"
                  onClick={() => updateCampaignStatus("suspended")}
                  disabled={isUpdating}
                >
                  <i className="ri-pause-line me-1"></i>
                  Suspend
                </Button>
              )}
              {campaign.status !== "completed" && (
                <Button
                  color="info"
                  size="sm"
                  className="rounded-pill"
                  onClick={() => updateCampaignStatus("completed")}
                  disabled={isUpdating}
                >
                  <i className="ri-check-line me-1"></i>
                  Complete
                </Button>
              )}
              {campaign.status !== "cancelled" && (
                <Button
                  color="danger"
                  size="sm"
                  outline
                  className="rounded-pill"
                  onClick={() => updateCampaignStatus("cancelled")}
                  disabled={isUpdating}
                >
                  <i className="ri-close-line me-1"></i>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
        <Button color="secondary" className="rounded-pill px-4" onClick={tog_center}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
